import React, { useCallback, useContext, useEffect, useState } from "react";
import inputBgLg from "@assets/images/backgrounds/inputBgLg.webp";
import inputBgMd from "@assets/images/backgrounds/inputBgMd.webp";
import useApiHook from "@hooks/useApiHook";
import useApiError from "@hooks/useApiError";
import {
  checkIfContainsSpecialCharacter,
  containsLowerCase,
  containsNumber,
  containsUpperCase,
  isLengthGreaterThanOrEqualToEight,
} from "@src/utils/PasswordHelpers";
import checkbox from "@assets/images/mdi_check-bold.webp";
import useNotification from "@src/hooks/useNotification";
import instance from "@src/axios/axiosInstance";
import Cookies from "js-cookie";
import { AuthDispatchContext } from "@src/context/AuthContext";
import { UserDispatchContext } from "@src/context/UserContext";
import { ChangeEmailContext } from "@src/context/ChangeEmailContext";
import type { IChangePassword } from "@src/types/AppTypes";

const ChangePassword = React.forwardRef<HTMLDivElement, IChangePassword>(
  ({ changepasswordModal, modalBorder, setChangePasswordModal }, ref) => {
    const setAuth = useContext(AuthDispatchContext);
    const userDispatch = useContext(UserDispatchContext);
    const [current_password, setCurrent_password] = useState<string>("");
    const [passwordDropdown, setPasswordDropdown] = useState<boolean>(false);
    const [showCurrentPassword, setShowCurrentPassword] =
      useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] =
      useState<boolean>(false);
    const [password, setPassword] = useState<string>("");
    const [confirm_password, setConfirm_password] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const setApiError = useApiError();
    const updatePasswordEndPoint = useApiHook({
      method: "post",
      url: "/users/change_password",
    });
    const { setEmailData } = useContext(ChangeEmailContext);
    const [hasEightChars, setHasEightChars] = useState<boolean>(false);
    const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
    const [hasLowerAndUpperChar, setHasLowerAndUpperChar] =
      useState<boolean>(false);
    const [hasNumber, setHasNumber] = useState<boolean>(false);
    const flashNotification = useNotification();

    const updatePassword = useCallback(
      async (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        current_password: string,
        password: string,
        confirm_password: string
      ) => {
        e.preventDefault();
        try {
          setIsLoading(true);
          const res = await updatePasswordEndPoint<{
            status: boolean;
            message: string;
          }>({ user: { current_password, password, confirm_password } });

          if (res && res.data.status === true) {
            flashNotification(res.data);
          }
          if (res && res.data.status === false) {
            flashNotification(res.data);
          }
          setChangePasswordModal(false);
          setConfirm_password("");
          setPassword("");
          setCurrent_password("");
          delete instance.defaults.headers.common["Authorization"];
          Cookies.remove("aT");
          setEmailData({
            type: "changeEmail",
            payload: { email: "", password: "" },
          });
          Cookies.remove("jwt_refresh_token");
          userDispatch({ type: "setUser", payload: null });
          setAuth({ type: "update", payload: false });
        } catch (e: any) {
          setApiError(e);
        } finally {
          setIsLoading(false);
        }
      },
      [
        setAuth,
        userDispatch,
        setApiError,
        updatePasswordEndPoint,
        setChangePasswordModal,
        flashNotification,
        setEmailData,
      ]
    );

    useEffect(() => {
      if (password.length) {
        setHasNumber(containsNumber(password));
        setHasLowerAndUpperChar(containsLowerCase(password));
        setHasLowerAndUpperChar(containsUpperCase(password));
        setHasEightChars(isLengthGreaterThanOrEqualToEight(password));
        setHasSpecialChar(checkIfContainsSpecialCharacter(password));
      }
    }, [password]);

    // back bg scroll stop :-

    useEffect(() => {
      const header = document.getElementById("header");
      const footer = document.getElementById("footer");

      document.body.style.overflow = "hidden";
      document.body.style.width = "100%";
      document.body.style.touchAction = "none";

      if (header) {
        header.style.zIndex = "0";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }
      return () => {
        // Enable background scroll
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "auto";
        document.body.style.width = "";
        document.body.style.touchAction = "";
        if (header) {
          header.style.zIndex = "";
        }
        if (footer) {
          footer.style.zIndex = "";
        }
      };
    }, []);

    return (
      <div
        className={`fixed w-full h-full bg-[#00000080] top-0 left-0 flex items-center justify-center z-[999] modal-content overflow-hidden ${
          changepasswordModal ? "block" : "hidden"
        }`}
      >
        <div
          id="change-password-form"
          onAnimationEnd={() => {
            document
              .getElementById("change-password-form")
              ?.classList.remove("form-shake");
          }}
          ref={ref}
          className="max-w-xl w-full bg-gradient-to-b from-[#1D1159] to-[#00AEEF] relative rounded-[5px_30px_40px_0px] max-sm:max-w-[95%]"
        >
          <img
            src={modalBorder}
            className="absolute top-0 left-0 w-full h-full z-[0]"
            alt=""
          />
          <div className="relative z-10 px-10 my-6 max-md:px-6 max-h-[78dvh] overflow-auto max-sm:px-5">
            <div className="grid grid-cols-[36px_1fr_36px] items-center">
              <div></div>
              <h2 className="text-primary text-center uppercase text-3xl max-md:text-2xl max-sm:text-xl">
                Change Password
              </h2>
              <button
                onClick={() => {
                  setChangePasswordModal(!changepasswordModal);
                  setConfirm_password("");
                  setCurrent_password("");
                  setPassword("");
                }}
                className="text-primary duration-150 hover:scale-95"
                aria-label="Close"
              >
                <i className="ri-close-circle-line text-4xl max-md:text-2xl"></i>
              </button>
            </div>

            <div className="text-center mt-10 max-md:mt-3">
              <div className="grid grid-cols-1 gap-7 max-md:gap-4 max-sm:grid-cols-1 text-start mb-8 max-md:mb-6">
                <div>
                  <label
                    className="text-primary text-base mb-1 f-family-mochiy-pop block"
                    htmlFor="accountOldPass"
                  >
                    Old Password *
                  </label>
                  <div className="relative">
                    <input
                      id="accountOldPass"
                      value={current_password}
                      onChange={(e) => setCurrent_password(e.target.value)}
                      className="relative p-5 pr-12 z-10 text-base w-full bg-transparent text-black placeholder:text-[#9CA3AF] f-family-mochiy-pop"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter password"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "NumpadEnter") {
                          e.preventDefault();
                          document.getElementById("accountNewPass")?.focus();
                        }
                      }}
                    />
                    <img
                      className="absolute top-0 left-0 w-full h-full"
                      src={inputBgLg}
                      alt=""
                    />
                    <i
                      onClick={() => {
                        setShowCurrentPassword(!showCurrentPassword);
                      }}
                      className={`hover:cursor-pointer text-black z-[10] absolute right-5 top-2/4 -translate-y-2/4 ${
                        showCurrentPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                      } text-2xl`}
                    ></i>
                  </div>
                </div>
                <div className="relative">
                  <label
                    className="text-primary text-base mb-1 f-family-mochiy-pop block"
                    htmlFor="accountNewPass"
                  >
                    New Password *
                  </label>
                  <div className="relative">
                    <input
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "NumpadEnter") {
                          e.preventDefault();
                          document.getElementById("accountConfPass")?.focus();
                        }
                      }}
                      onFocus={() => {
                        setPasswordDropdown(true);
                      }}
                      id="accountNewPass"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="relative p-5 pr-12 z-10 text-base w-full bg-transparent text-black placeholder:text-[#9CA3AF] f-family-mochiy-pop"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter password"
                      onBlur={() => {
                        setPasswordDropdown(false);
                      }}
                    />
                    <img
                      className="absolute top-0 left-0 w-full h-full"
                      src={inputBgMd}
                      alt=""
                    />
                    <i
                      onClick={() => {
                        setShowNewPassword(!showNewPassword);
                      }}
                      className={`hover:cursor-pointer text-black z-[10] absolute right-5 top-2/4 -translate-y-2/4 ${
                        showNewPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                      } text-2xl`}
                    ></i>
                  </div>
                  {password.length > 0 && passwordDropdown && (
                    <div className="absolute bg-[#1d1159] w-full p-6 z-[12] rounded-[5px_27px_19px_25px] bg-[length:100%_100%] bg-no-repeat bg-[url('@assets/images/borders/video-border.webp')]">
                      <div className="flex justify-between items-center gap-3 mb-1">
                        <span className="text-xl text-primary-gradient">
                          8 Characters
                        </span>
                        {isLengthGreaterThanOrEqualToEight(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-1 gap-3">
                        <span className="text-xl text-primary-gradient">
                          1 Uppercase Letter
                        </span>
                        {containsUpperCase(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="flex justify-between items-center gap-3 mb-1">
                        <span className="text-xl text-primary-gradient">
                          1 Lowercase Letter
                        </span>
                        {containsLowerCase(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="items-center flex justify-between mb-1 gap-3">
                        <span className="text-xl text-primary-gradient">
                          1 number
                        </span>
                        {containsNumber(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="flex justify-between gap-3 items-center">
                        <span className="text-primary-gradient text-xl">
                          1 special Character
                        </span>
                        {checkIfContainsSpecialCharacter(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label
                    className="text-primary text-base mb-1 f-family-mochiy-pop block"
                    htmlFor="accountConfPass"
                  >
                    Confirm Password*
                  </label>
                  <div className="relative">
                    <input
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "NumpadEnter") {
                          e.preventDefault();
                          if (
                            confirm_password.length >= 8 &&
                            hasEightChars &&
                            hasLowerAndUpperChar &&
                            hasSpecialChar &&
                            hasNumber
                          ) {
                            document.getElementById("accountConfPass")?.blur();
                            updatePassword(
                              {
                                preventDefault: () => {},
                              } as unknown as React.MouseEvent<
                                HTMLButtonElement,
                                MouseEvent
                              >,
                              current_password,
                              password,
                              confirm_password
                            );
                          }
                        }
                      }}
                      id="accountConfPass"
                      value={confirm_password}
                      type={showConfirmPassword ? "text" : "password"}
                      onChange={(e) => setConfirm_password(e.target.value)}
                      className="relative p-5 pr-12 z-10 text-base w-full bg-transparent text-black placeholder:text-[#9CA3AF] f-family-mochiy-pop"
                      placeholder="Enter password"
                    />
                    <img
                      className="absolute top-0 left-0 w-full h-full"
                      src={inputBgMd}
                      alt=""
                    />
                    <i
                      onClick={() => {
                        setShowConfirmPassword(!showConfirmPassword);
                      }}
                      className={`hover:cursor-pointer text-black z-[10] absolute right-5 top-2/4 -translate-y-2/4 ${
                        showConfirmPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                      } text-2xl`}
                    ></i>
                  </div>
                </div>
              </div>
              <button
                disabled={
                  !hasEightChars ||
                  !hasLowerAndUpperChar ||
                  !hasNumber ||
                  !hasSpecialChar ||
                  confirm_password.length < 8 ||
                  isLoading
                }
                onClick={(e) => {
                  if (current_password === password) {
                    flashNotification({
                      message:
                        "Old password and new password should not be match.",
                      status: false,
                    });
                  } else {
                    updatePassword(
                      e,
                      current_password,
                      password,
                      confirm_password
                    );
                  }
                }}
                className="text-2xl max-md:text-xl customized-button w-full"
                aria-label="Update"
              >
                {isLoading ? (
                  <div className="flex justify-center">
                    <i className="ri-loop-right-line btn-loader"></i>
                  </div>
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default ChangePassword;
